import * as modules from '@sdk/api/modules'
import { EventEmitter } from '@sdk/utils/eventEmitter'
import { LowLevelApi } from '@sdk/api/LowLevelApi'
import type { PitcherEvent, ApiOptions } from '@sdk/interfaces'

/**
 * Creates a high-level API instance and caches it. Reterns a new instance only if new options are provided.
 */
export function createHighLevelApi(options?: ApiOptions) {
  const API = new LowLevelApi(options)
  const eventEmitter = new EventEmitter()

  API.on('event', (event: PitcherEvent) => {
    eventEmitter.emit(event.type, event.body)
  })

  return {
    API,
    ...modules,
    /**
     * Subscribe to a given event type by its key.
     *
     * @param {string} type - The event type to subscribe to.
     * @param {(payload: PitcherEvent) => void} callback - The callback function to handle the event.
     * @returns A Promise resolving to a cleanup function to unsubscribe from the event.     * @example
     *
     * @example
     * const unsubscribeFromSectionListUpdate = usePitcherApi().on('entered_fullscreen', (event: object) => {
     *   // handle event
     * })
     * // later
     * unsubscribeFromSectionListUpdate()
     */
    on: (type: string, callback: (payload: PitcherEvent) => void) => eventEmitter.on(type, callback),

    /**
     * Unsubscribe from a given event type by its key and the attached callback reference.
     * @param {string} type - The event type to unsubscribe from.
     * @param {(payload: PitcherEvent) => void} callback - The callback function reference to remove.
     *
     * @example
     * const callback = (event: object) => {
     *   // handle event
     * }
     * usePitcherApi().on('entered_fullscreen', callback)
     * // later
     * usePitcherApi().off('entered_fullscreen', callback)
     */
    off: (type: string, callback: (payload: PitcherEvent) => void) => eventEmitter.off(type, callback),

    /**
     * Make the entire CatalogIQ fullscreen.
     *
     * @example
     * usePitcherApi().enterFullscreen()
     */
    enterFullscreen: () => API.request('enter_fullscreen'),

    /**
     * Exit fullscreen mode.
     *
     * @example
     * usePitcherApi().exitFullscreen()
     */
    exitFullscreen: () => API.request('exit_fullscreen'),

    /**
     * Return is CatalogIQ in fullscreen.
     *
     * @example
     * usePitcherApi().isFullscreen()
     */
    isFullscreen: () => API.request('is_fullscreen'),

    /**
     * Log out the current user.
     *
     * @example
     * usePitcherApi().logout()
     */
    logout: () => API.request('logout'),

    /**
     * Quit the current instance and go to the instance selection screen.
     *
     * @example
     * usePitcherApi().quitInstance()
     */
    quitInstance: () => API.request('quit_instance'),

    /**
     * Get the request types.
     *
     * @returns {Promise<any>}
     */
    getRequestTypes: () => API.request('get_request_types'),

    /**
     * Broadcast an event to CatalogIQ.
     * @param {PitcherEvent} event - The event to broadcast.
     *
     * @example
     * usePitcherApi()
     *   .broadcast({
     *     type: "canvas_updated",
     *     body: { context: { myContextProperty: 'test' } },
     *   })
     *   .then(function (result) {
     *     useUi().toast({
     *       message: "Canvas Populated.",
     *       type: "info",
     *     })
     *   })
     */
    broadcast: (event: PitcherEvent) => API.request('broadcast', event),

    /**
     * Subscribe to updates.
     *
     * @returns {Promise<void>}
     */
    subscribe: () => API.request('subscribe'),

    /**
     * Unsubscribe from updates.
     *
     * @returns {Promise<void>}
     */
    unsubscribe: () => API.request('unsubscribe'),
  }
}
